import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Grid,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { ReceiptLongOutlined } from "@mui/icons-material";
import { FormEvent, useState, useContext } from "react";
import { POST } from "../../../Utilities/BaseService";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import { AppContext } from "../../../Utilities/AppContext";

export const CollectConnectionCharges = () => {
    const theme = useTheme();
    const { user } = useContext(AppContext);

    const [formData, setFormData] = useState({
        meterNo: "",
        collectionDate: new Date().toISOString().split("T")[0],
        paymentMethod: null as any,
        amount: "",
        adjustment: "",
        description: "Connection Charges Collection",
    });
    const [clear, setClear] = useState(false)

    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({
        message: "",
        show: false,
        severity: "info" as "info" | "success" | "error",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();

        if (!formData.meterNo) {
            setFeedback({
                show: true,
                message: "Meter No. is required.",
                severity: "error",
            });
            return;
        }

        if (!formData.paymentMethod) {
            setFeedback({
                show: true,
                message: "Payment Method is required.",
                severity: "error",
            });
            return;
        }

        setLoading(true);
        setFeedback({ show: false, message: "", severity: "info" });

        try {
            const payload = {
                meterNo: formData.meterNo,
                amount: parseFloat(formData.amount || "0"),
                adjustment: parseFloat(formData.adjustment || "0"),
                collectionDate: formData.collectionDate,
                paymentMethod: formData.paymentMethod.id,
                description: formData.description,
            };

            const response = await POST("/reading/collection/collect-connection-charges", payload);

            if (response.status === 201 || response.data?.success) {
                setFeedback({
                    show: true,
                    message: response.data?.message || "Connection charges collected successfully!",
                    severity: "success",
                });
                setFormData({
                    meterNo: "",
                    collectionDate: new Date().toISOString().split("T")[0],
                    paymentMethod: null,
                    amount: "",
                    adjustment: "",
                    description: "Connection Charges Collection",
                });
                setClear(true)
            } else {
                setFeedback({
                    show: true,
                    message: response.data?.message || "Failed to collect connection charges.",
                    severity: "error",
                });
            }
        } catch (err: any) {
            console.error(err);
            setFeedback({
                show: true,
                message: err.response?.data?.message || "An error occurred while processing the collection.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold", color: "GrayText" }}>
                Collect Connection Charges
            </Typography>

            {feedback.show && (
                <Alert severity={feedback.severity} sx={{ mb: 3 }}>
                    {feedback.message}
                </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            label="Meter No."
                            name="meterNo"
                            value={formData.meterNo}
                            onChange={handleChange}
                            placeholder="Enter Meter Number"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            label="Collection Date"
                            type="date"
                            name="collectionDate"
                            value={formData.collectionDate}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <UpdatedSearchableInput
                            api="/reading/payment-method"
                            label="Payment Method"
                            _name="paymentMethod"
                            setOutput={(val) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    paymentMethod: val,
                                }));

                            }}
                            clearAll={clear}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            
                            fullWidth
                            label="Amount"
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            inputProps={{ min: 0, step: "0.01" }}
                            placeholder="0.00"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Adjustment"
                            type="number"
                            name="adjustment"
                            value={formData.adjustment}
                            onChange={handleChange}
                            inputProps={{ min: 0, step: "0.01" }}
                            placeholder="0.00"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            color="secondary"
                            disabled={loading}
                            endIcon={
                                loading ? (
                                    <CircularProgress size="1rem" color="inherit" />
                                ) : (
                                    <ReceiptLongOutlined />
                                )
                            }
                            sx={{ py: 1.5 }}
                        >
                            Collect Connection Charges
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};
